import React, { useState } from "react";
import PropTypes from "prop-types";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import ClearIcon from "@mui/icons-material/Clear";
import { CircularProgress, MenuItem, Select, IconButton } from "@mui/material";
import colors from "assets/theme/base/colors";

const SuiDropDownList = ({ 
  data, 
  select, 
  disabled, 
  readOnly = false, 
  isLoading, 
  onBlur,
  isClearable = false,
  onClear 
}) => {
   
  // State to track if the select field is being hovered
  const [isHovered, setIsHovered] = useState(false);
   
  // Check if the current value is empty (placeholder is showing)
  const isShowingPlaceholder = !select.val && select.val !== 0;

  const handleClearSelection = (event) => {
    // Stop propagation and prevent default to ensure the event doesn't trigger Select dropdown
    event.stopPropagation();
    event.preventDefault();
    
    select.fn({ target: { value: "" } });
    
    if(typeof onClear === 'function'){
      onClear();
    }
  };

  // Render the end adornment with clear button separately from dropdown arrow
  const renderEndAdornment = () => {
    if (isLoading) {
      return (
        <span style={{ position: "absolute", right: 6, top: 7 }}>
          <CircularProgress size={30} sx={{ color: colors.info.main }} />
        </span>
      );
    }
    
    if (isClearable && !isShowingPlaceholder && isHovered) {
      return (
        <IconButton
          onClick={handleClearSelection}
          size="small"
          tabIndex={-1}
          sx={{ 
            padding: 0,
            position: 'absolute', 
            right: '36px',
            top: '9px',
            fontSize: '20px', 
            zIndex: 2,
            width: '20px',
            height: '20px',
            opacity: 1,
            transition: 'opacity 0.2s ease-in-out'
          }}
        >
          <ClearIcon sx={{ fontSize: 16, color: "#848484" }} />
        </IconButton>
      );
    }
    
    return null;
  };

  return (
    <div 
      style={{ position: 'relative' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {renderEndAdornment()}
      <Select
        onBlur={onBlur}
        readOnly={readOnly}
        disabled={disabled}
        name={select.name}
        value={isLoading ? "" : select.val}
        IconComponent={({ className }) => (
          <KeyboardArrowDownRoundedIcon
            className={className}
            sx={{ fontSize: 12, color: "#848484" }}
          />
        )}
        displayEmpty
        MenuProps={{
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "left",
          },
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: '2px !important',
            fontSize:'small !important'
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderRadius: '2px !important',
            fontSize:'small !important'
          },
          "& .MuiSelect-select": {
            borderRadius: '2px !important',
            fontSize:'small !important',
            color: isShowingPlaceholder ? 'darkgrey !important' : '#151515',
            paddingRight: isClearable && !isShowingPlaceholder ? '60px !important' : '32px !important'
          },
          "& .MuiSelect-icon": {
            borderRadius: '2px !important',
            marginRight: '4px'
          },
          "& .css-mlog3m-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input": {
            borderRadius: '2px !important',
            fontSize:'small !important'
          },
        }}
        onChange={(e) => {
          select.fn(e);
        }}
      >
        <MenuItem value="" disabled sx={{ fontSize: "0.875rem" }}>
          {select.placeholder || select.label}
        </MenuItem>
        {data &&
          data.map((item) => (
            <MenuItem
              key={item[select.key] || item}
              value={select.valueKey ? item[select.valueKey] : item}
              disabled={select.enableId && (select.enableId === item ? false : true)}
              sx={{ fontSize: "0.875rem" }} // Font size set to small
            >
              {item[select.labelName] || item}
            </MenuItem>
          ))}
      </Select>
    </div>
  );
};

SuiDropDownList.propTypes = {
  data: PropTypes.any,
  select: PropTypes.shape({
    label: PropTypes.string,
    name: PropTypes.string,
    val: PropTypes.any,
    fn: PropTypes.func,
    key: PropTypes.string,
    valueKey: PropTypes.string,
    labelName: PropTypes.string,
    enableId: PropTypes.any,
    placeholder: PropTypes.string,
  }),
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  isLoading: PropTypes.bool,
  onBlur: PropTypes.func,
  isClearable: PropTypes.bool,
  onClear: PropTypes.func,
};

export default SuiDropDownList;